import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useThemeContext } from "../context/themeContext";

/**
 * Shared section header for page sections (Explore, the profile) — used by
 * Carousel and on its own.
 *
 * Renders a bold title on the left and an optional action button on the right.
 * When `info` is provided, a small info icon is rendered immediately after
 * the title; tapping it opens a popover with the supplied content.
 *
 * @param {string}              title       - Section title
 * @param {string}              buttonLabel - Optional action button label
 * @param {string}              buttonHref  - Optional action button destination
 * @param {object}              buttonState - Optional router state passed with buttonHref
 * @param {function}            onButtonClick - Optional action button handler, instead of buttonHref
 * @param {boolean}             buttonDisabled - Show the action button greyed out, with no
 *                                            destination yet (a link to a page not built yet)
 * @param {ReactNode}           buttonIcon  - Optional icon: renders the action as a primary-coloured
 *                                            icon button (buttonLabel becomes its tooltip/aria-label)
 * @param {ReactNode}           icon        - Optional icon before the title, in the primary
 *                                            colour (as the profile stats section titles)
 * @param {string|ReactNode}    info        - Optional explanatory content shown
 *                                            in a popover triggered by an info
 *                                            icon next to the title. Strings
 *                                            are wrapped in a Typography so
 *                                            callers don't have to.
 */
export default function SectionHeader({
  title,
  buttonLabel,
  buttonHref,
  buttonState,
  onButtonClick,
  buttonDisabled = false,
  buttonIcon,
  icon,
  info,
}) {
  const { isTouchDevice } = useThemeContext();
  const showButton =
    buttonLabel && (buttonHref || onButtonClick || buttonDisabled);
  // A destination renders as a real link, so crawlers can follow it and a
  // modified click opens a new tab; a handler-only action stays a plain button.
  // Touch devices get no native long-press link menu, matching the collection cards.
  const buttonProps = onButtonClick
    ? { onClick: onButtonClick }
    : buttonHref && !buttonDisabled
      ? {
          component: RouterLink,
          to: buttonHref,
          state: buttonState,
          ...(isTouchDevice && {
            onContextMenu: (e) => e.preventDefault(),
            sx: { WebkitTouchCallout: "none" },
          }),
        }
      : {};

  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const infoOpen = Boolean(infoAnchorEl);

  return (
    <Stack
      direction="row"
      justifyContent={{ xs: "space-between", sm: "flex-start" }}
      alignItems={"center"}
      mb={0.25}
      spacing={{ sm: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {/* Extra 2px so the icon sits 6px from the title while the info icon
            keeps the row's 4px. Padding, not margin: Stack's spacing resets its
            children's margins. */}
        {icon && <Box sx={{ display: "flex", pr: 0.25 }}>{icon}</Box>}
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        {info && (
          <>
            <IconButton
              size="small"
              aria-label={`About ${title}`}
              onClick={(e) => setInfoAnchorEl(e.currentTarget)}
              sx={{ color: "text.secondary" }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
            <Popover
              open={infoOpen}
              anchorEl={infoAnchorEl}
              onClose={() => setInfoAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              slotProps={{
                paper: {
                  sx: { p: 1.5, maxWidth: 320 },
                },
              }}
            >
              {typeof info === "string" ? (
                <Typography variant="body2">{info}</Typography>
              ) : (
                info
              )}
            </Popover>
          </>
        )}
      </Stack>
      {showButton &&
        (buttonIcon ? (
          <Tooltip title={buttonLabel}>
            <IconButton
              color="primary"
              size="small"
              aria-label={buttonLabel}
              {...buttonProps}
            >
              {buttonIcon}
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="text"
            size="small"
            {...buttonProps}
            disabled={buttonDisabled}
          >
            {buttonLabel}
          </Button>
        ))}
    </Stack>
  );
}
